/**
 * **Nomor pesanan** — nomor yang DIUCAPKAN manusia, bukan identitas mesin.
 *
 * ⚠️ Jangan tertukar dengan `order.id` (UUIDv7, K3). Yang itu identitas: unik global, dipakai
 * idempotensi sync, tak pernah dibaca siapa pun. Yang INI dicetak di struk, disebut kasir, dan
 * didengar tamu — jadi ia harus pendek, dan ia harus **tak mungkin kembar**.
 *
 * ### ⛔⛔⛔ Bentuk lama, dan kenapa ia bom waktu
 * ```ts
 * orderNo: `UI/${String(Date.now()).slice(-6)}/1`
 * ```
 * Enam digit terakhir `Date.now()` **berulang tiap ±16,7 menit** (10⁶ milidetik), sementara
 * penjaganya berlaku SELAMANYA:
 * ```sql
 * CREATE UNIQUE INDEX "order_no_uniq" ON "order" ("outlet_id","order_no");
 * ```
 * ⇒ nomor bill adalah undian dari sejuta kotak yang **tak pernah dikosongkan**. Dengan ±300
 * bill/hari, tabrakan menumpuk lewat paradoks ulang tahun; dalam hitungan minggu ia bukan lagi
 * kemungkinan. Akibatnya `INSERT` ditolak indeks ⇒ **pesanan gagal dibuka di depan tamu**, dengan
 * pesan yang tak menyebut sebabnya.
 *
 * ### ⭐⭐⭐ Bentuk baru: `<PERANGKAT>/<HARI>/<URUT>`
 * ```
 * HP_KASIR/260824/007
 * ```
 * Keunikannya **struktural, bukan probabilistik** — dan buktinya sudah ada di skema:
 * ```sql
 * CREATE UNIQUE INDEX "device_code_uniq" ON "device" ("outlet_id","device_code");
 * ```
 * `device_code` unik **per outlet** — cakupan yang SAMA PERSIS dengan `order_no_uniq`. Jadi dua
 * perangkat di satu outlet tak mungkin punya prefiks sama, dan satu perangkat menghitung urutnya
 * sendiri. ⇒ **tak ada wasit yang dibutuhkan**, tak ada jaringan yang dibutuhkan, dan dua till yang
 * sama-sama luring tetap tak bisa menerbitkan nomor kembar.
 *
 * ⛔ Bukan `MAX()+1` — `research/KIOSK-TEARDOWN.md` mencatatnya sebagai cacat VTEC: ia menuntut
 * membaca dulu, dan yang membaca bersamaan mendapat jawaban sama.
 *
 * ⚠️ **Lompatan nomor itu SAH.** Urut dinaikkan saat nomor dipakai, bukan saat pesanan berhasil
 * dibuka — percobaan yang gagal membakar satu nomor. Celah di deretan jauh lebih murah daripada
 * nomor kembar: yang pertama pertanyaan, yang kedua dua bill yang tak bisa dibedakan di telinga
 * tamu. (⛔ Ini juga bukan nomor faktur pajak — di situ celah memang tak boleh.)
 */
/** Lebar minimal urut lokal. ⚠️ Bukan PLAFON: urut ke-1000 jadi `1000`, tak pernah berputar. */
export const ORDER_SEQ_PAD = 3;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** ⚠️ Prefiks disaring, bukan ditolak: `device_code` boleh apa saja, `order_no` harus bisa dibaca. */
const UNSAFE = /[^A-Z0-9_-]/g;
/**
 * Prefiks perangkat untuk nomor pesanan.
 *
 * ⛔ Diambil dari `device_code` UTUH, bukan potongannya. Dua perangkat `POS-SMB-01` dan
 * `KDS-SMB-01` yang dipangkas jadi `01` akan bertabrakan — dan tabrakan itu tak akan terlihat
 * sampai ada dua bill dengan nomor sama di laporan yang sama.
 */
export function orderNoPrefix(deviceCode) {
    const bersih = deviceCode.trim().toUpperCase().replace(UNSAFE, '');
    if (bersih === '') {
        throw new Error(`device_code tak menghasilkan prefiks yang bisa dipakai: "${deviceCode}"`);
    }
    return bersih;
}
/** `2026-08-24` → `260824`. ⚠️ Hari USAHA, bukan jam perangkat — lihat §2 (`BDATE`). */
function padatkanTanggal(businessDate) {
    if (!DATE_RE.test(businessDate)) {
        throw new Error(`business_date wajib YYYY-MM-DD, dapat "${businessDate}"`);
    }
    return businessDate.slice(2).replace(/-/g, '');
}
export function formatOrderNo(input) {
    if (!Number.isInteger(input.seq) || input.seq < 1) {
        throw new Error(`urut nomor pesanan wajib bilangan bulat ≥ 1, dapat ${String(input.seq)}`);
    }
    const urut = String(input.seq).padStart(ORDER_SEQ_PAD, '0');
    return `${orderNoPrefix(input.deviceCode)}/${padatkanTanggal(input.businessDate)}/${urut}`;
}
/**
 * Kebalikan `formatOrderNo`. `null` untuk apa pun yang bukan bentuk ini — **termasuk nomor lama**
 * (`UI/414821/1`), dan itu disengaja: yang lama tak punya urut yang berarti, jadi menebaknya
 * justru menghasilkan urut palsu yang dipakai server untuk menyarankan nomor berikutnya.
 */
export function parseOrderNo(orderNo) {
    /*
     * ⛔⛔ `\d{3,}` pada urutnya BUKAN kosmetik — ia yang memisahkan bentuk baru dari yang lama.
     * `UI/414821/1` lolos setiap bagian lain: `UI` prefiks yang sah, `414821` enam digit, `1` angka.
     * Yang membedakannya cuma **padding**: bentuk baru selalu ≥ 3 digit (`ORDER_SEQ_PAD`), yang lama
     * selalu tepat `1`. Tanpa penjaga ini, jam yang jadi `414821` dibaca sebagai hari usaha dan
     * urutnya dibaca `1` — lalu server menyarankan `2` sebagai nomor bebas berikutnya, dan tabrakan
     * yang mau diperbaiki justru diulang dengan percaya diri.
     */
    const m = /^([A-Z0-9_-]+)\/(\d{6})\/(\d{3,})$/.exec(orderNo);
    if (m === null)
        return null;
    const seq = Number(m[3]);
    if (!Number.isSafeInteger(seq) || seq < 1)
        return null;
    return { prefix: m[1], day: m[2], seq };
}
//# sourceMappingURL=order-no.js.map